import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api, packTrackToPlayable } from "@/lib/api";
import { splitGenres } from "@/components/GenrePill";
import { GenreFilter } from "@/components/GenreFilter";
import { PageLoading, LoadError } from "@/components/LoadState";
import { TrackList } from "@/components/TrackRow";
import { CompactTrackList } from "@/components/CompactTrackList";

export default function PoolPage({ config }) {
  const [params, setParams] = useSearchParams();
  const genre = params.get("genre") || "";
  const selected = genre.split(",").map((g) => g.trim().toLowerCase()).filter(Boolean);
  const toggleGenre = (name) => { const k = name.toLowerCase(); const next = selected.includes(k) ? selected.filter((x) => x !== k) : [...selected, k]; const names = next.map((x) => genres.find((g) => g.name.toLowerCase() === x)?.name || x); names.length ? setParams({ genre: names.join(",") }) : setParams({}); };
  const [genres, setGenres] = useState([]);
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);

  const [all, setAll] = useState([]);

  useEffect(() => {
    setLoading(true); setError(false);
    Promise.all([api.get("/tracks?limit=5000"), api.get("/packs")])
      .then(([t, p]) => {
        const pistas = p.data.flatMap((pack) => (pack.tracks || []).filter((pt) => !pt.track_id).map((pt) => packTrackToPlayable(pack, pt)));
        setAll([...t.data, ...pistas]);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [attempt]);

  useEffect(() => {
    const counts = {};
    all.forEach((t) => splitGenres(t.genre).forEach((g) => { const k = g.toLowerCase(); counts[k] = counts[k] ? { ...counts[k], count: counts[k].count + 1 } : { name: g, count: 1 }; }));
    setGenres(Object.values(counts).sort((a, b) => b.count - a.count || a.name.localeCompare(b.name)));
    const sel = genre.split(",").map((g) => g.trim().toLowerCase()).filter(Boolean);
    setTracks(sel.length ? all.filter((t) => splitGenres(t.genre).some((g) => sel.includes(g.toLowerCase()))) : all);
  }, [all, genre]);

  const inGenreHL = (t) => (t.highlight_genres || []).some((g) => selected.includes(g.toLowerCase()));
  const byDate = [...tracks].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
  const latest = byDate.slice(0, 20);
  const rest = byDate.slice(4);

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14">
      <div className="mb-8">
        <div className="font-mono text-xs text-red-500 uppercase tracking-[0.3em] mb-2">Selección para DJs</div>
        <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tight text-white">Pool</h1>
        {!loading && !error && <div className="font-mono text-[10px] text-white/40 uppercase tracking-[0.3em] mt-3" data-testid="pool-count">{tracks.length} tracks{genre ? ` · ${genre.split(",").join(" + ")}` : ""}</div>}
      </div>

      <GenreFilter prefix="pool" genres={genres} selected={selected} onToggle={toggleGenre} onClear={() => setParams({})} />

      {loading ? (
        <PageLoading testid="pool-loading" />
      ) : error ? (
        <LoadError onRetry={() => setAttempt((a) => a + 1)} testid="pool-error" />
      ) : tracks.length === 0 ? (
        <div className="border border-dashed border-white/10 rounded-2xl py-24 text-center text-white/40">
          No hay temas en este género todavía. <Link to="/pool" className="text-red-500">Ver todos</Link>
        </div>
      ) : (
        <div className="space-y-12">
          {!genre && (
            <section data-testid="pool-latest">
              <div className="font-mono text-[10px] text-red-500 uppercase tracking-[0.3em] mb-4">Lo último · recién subido</div>
              <TrackList tracks={latest} config={config} perPage={4} sideNav testid="pool-latest-grid" />
            </section>
          )}
          <section data-testid="pool-all">
            <div className="font-mono text-[10px] text-white/40 uppercase tracking-[0.3em] mb-4">{genre ? genre.split(",").join(" + ") : "Todo el pool"}</div>
            {genre ? (
              <div className="space-y-10">
                {tracks.some(inGenreHL) && (
                  <div data-testid="pool-genre-highlights">
                    <div className="font-mono text-[10px] text-red-500 uppercase tracking-[0.3em] mb-3">Destacados · {tracks.filter(inGenreHL).length}</div>
                    <TrackList tracks={tracks.filter(inGenreHL).sort((a, b) => new Date(b.highlighted_at || b.created_at || 0) - new Date(a.highlighted_at || a.created_at || 0))} config={config} perPage={6} grid testid="pool-genre-grid" />
                  </div>
                )}
                <CompactTrackList tracks={byDate} />
              </div>
            ) : <CompactTrackList tracks={rest} />}
          </section>
        </div>
      )}
    </div>
  );
}

